import { liveReload } from '@shared/services/realtime.service';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ExecChartComponent, fmtValue } from './exec-chart.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { UsersService } from '@shared/services/entities/users.service';
import {
  DashboardSummaryService, DashSummary, DashMonth, DashLevel,
} from '@shared/services/entities/dashboard-summary.service';

const LEVEL_LABEL: Record<DashLevel, string> = { urgent: 'Urgente', today: 'Hoy', week: 'Esta semana' };
const MONTH_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

/**
 * Inicio · Centro de pendientes.
 *
 * Lo que se ve depende del usuario y lo decide el API (/dashboard/summary):
 * solo sus hospitales y especialidades (Usuarios → Parámetros) y solo los
 * bloques de los módulos que tiene en su rol. Esta pantalla no filtra nada
 * por su cuenta: pinta lo que el servidor manda.
 */
@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [DecimalPipe, FormsModule, MatIconModule, MatButtonModule, MatTooltipModule, PageTitleComponent, PageLoaderComponent, ExecChartComponent],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {
  private router = inject(Router);
  private usersService = inject(UsersService);
  private service = inject(DashboardSummaryService);

  readonly pageLoad = new PageLoad();

  /** Tiempo real: recarga cuando otro usuario cambia estos módulos (WebSocket). */

  private readonly live = liveReload(['interventions', 'remissions', 'incidents', 'stock', 'shippings', 'transfers', 'materialRequests', 'custody', 'supportTickets'], () => this.pageLoad.ready && this.reload());
  readonly data = signal<DashSummary | null>(null);
  readonly levelLabel = LEVEL_LABEL;
  readonly monthShort = MONTH_SHORT;

  place: string | null = null;
  /** Perfil que está viendo Dirección (null = el suyo). */
  view: string | null = null;
  readonly thisYear = new Date().getFullYear();
  year = this.thisYear;
  readonly years = [this.thisYear, this.thisYear - 1, this.thisYear - 2];
  readonly picked = signal<number | null>(null);   // mes abierto en la gráfica (1-12)

  userName = () => (this.usersService.currentUser()?.name ?? '').split(' ')[0];

  readonly greeting = computed(() => {
    const h = new Date().getHours();
    return h < 12 ? 'Buenos días' : h < 19 ? 'Buenas tardes' : 'Buenas noches';
  });
  readonly today = new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });

  readonly urgentCount = computed(() => (this.data()?.actions ?? []).filter(a => a.level === 'urgent').length);
  readonly mods = computed(() => this.data()?.modules ?? { folios: false, inventory: false, incidents: false, requests: false, transfers: false, custody: false });
  readonly anyModule = computed(() => Object.values(this.mods()).some(Boolean));

  // ── Gráfica mensual ──
  readonly chartMax = computed(() => {
    const ms = this.data()?.chart?.months ?? [];
    return Math.max(10, ...ms.map(m => m.folio + m.remission)) * 1.1;
  });
  readonly chartTotals = computed(() => {
    const ms = (this.data()?.chart?.months ?? []).filter(m => !m.future);
    const folio = ms.reduce((a, m) => a + m.folio, 0);
    const remission = ms.reduce((a, m) => a + m.remission, 0);
    const total = folio + remission;
    const top = ms.reduce<DashMonth | null>((a, m) => (!a || m.folio + m.remission > a.folio + a.remission ? m : a), null);
    return {
      folio, remission, total,
      avg: ms.length ? Math.round(total / ms.length) : 0,
      remPct: total ? Math.round(100 * remission / total) : 0,
      top: top && (top.folio + top.remission) ? `${top.label} · ${top.folio + top.remission}` : '—',
    };
  });
  readonly selectedMonth = computed(() => {
    const m = this.picked();
    return m ? (this.data()?.chart?.months ?? []).find(x => x.month === m) ?? null : null;
  });
  readonly selectedDelta = computed(() => {
    const m = this.selectedMonth();
    if (!m) return '';
    if (m.month === 1) return 'primer mes del año';
    const prev = (this.data()?.chart?.months ?? []).find(x => x.month === m.month - 1);
    const a = m.folio + m.remission, b = prev ? prev.folio + prev.remission : 0;
    if (!b) return 'sin datos del mes anterior';
    const d = Math.round(100 * (a - b) / b);
    return `${d >= 0 ? '▲' : '▼'} ${Math.abs(d)}% vs mes anterior`;
  });

  ngOnInit(): void { void this.init(); }
  init(): Promise<boolean> { return this.pageLoad.run(() => this.load()); }

  async load(): Promise<void> {
    const d = await this.service.summary(this.year, this.place, this.view);
    this.data.set(d);
    const chart = d?.chart;
    if (chart) {
      const cur = chart.months.find(m => m.month === chart.currentMonth && !m.future);
      const keep = this.picked() && chart.months.find(m => m.month === this.picked() && !m.future);
      this.picked.set(keep ? this.picked() : (cur && (cur.folio + cur.remission) ? cur.month : null));
    }
  }

  reload(): void { void this.pageLoad.refresh(this.load()); }

  go(route: string): void { void this.router.navigateByUrl(route); }

  pickMonth(m: DashMonth): void {
    if (m.future) return;
    this.picked.set(this.picked() === m.month ? null : m.month);
  }

  kpiValue(v: number, f: 'int' | 'money' | 'days'): string { return fmtValue(v, f); }

  barHeight(m: DashMonth): number { return Math.round(100 * (m.folio + m.remission) / this.chartMax()); }
  share(n: number, total: number): number { return total ? Math.round(1000 * n / total) / 10 : 0; }

  feedWhen(value: string): string {
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  }
}
